export const REVIEW_BUCKET = 'review-images';
export const MAX_REVIEW_IMAGES = 3;
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
export const ALLOWED_IMAGE_TYPES: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
};

export const NICKNAME_RE = /^[가-힣a-zA-Z0-9_]{2,12}$/;
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const PASSWORD_MIN = 8;

export function reviewImageUrl(path: string) {
  const base = process.env.NEXT_PUBLIC_SUPABASE_URL ?? '';
  const encoded = path.split('/').map(encodeURIComponent).join('/');
  return `${base}/storage/v1/object/public/${REVIEW_BUCKET}/${encoded}`;
}

export function makeExcerpt(text: string, max = 120) {
  const t = text.replace(/\s+/g, ' ').trim();
  return t.length > max ? `${t.slice(0, max)}…` : t;
}

// 한국시간 기준 YYYY-MM-DD
export function kstYmd(d: Date = new Date()) {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(d);
}

// 공개 날짜(YYYY-MM-DD) → 그날 한국시간 0시
export function kstStartOfDay(ymd: string) {
  return new Date(`${ymd}T00:00:00+09:00`);
}

// 공개 예약 중(아직 공개 전)인지
export function isScheduled(publishAt: string | null | undefined) {
  return !!publishAt && new Date(publishAt).getTime() > Date.now();
}

export function formatDateKo(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
  return parts.replace(/\s/g, '').replace(/\.$/, '');
}

type ProfileEmbed = { nickname: string } | { nickname: string }[] | null;
export function nicknameOf(p: ProfileEmbed) {
  if (!p) return '탈퇴한 회원';
  return Array.isArray(p) ? p[0]?.nickname ?? '탈퇴한 회원' : p.nickname;
}

// 회원 글은 프로필 닉네임, 관리자가 게시한 글(AI 생성/고객 인터뷰 정리)은 display_name
export function authorOf(r: {
  member_profiles: ProfileEmbed;
  display_name?: string | null;
  is_ai_generated?: boolean;
  is_interview?: boolean;
}) {
  if ((r.is_ai_generated || r.is_interview) && r.display_name) return r.display_name;
  return nicknameOf(r.member_profiles);
}

export const REVIEW_SELECT =
  'id, user_id, title, content, image_paths, is_hidden, is_ai_generated, is_interview, display_name, publish_at, created_at, member_profiles(nickname)';

// 관리자가 고객 이야기 글에 사진을 추가할 때 쓰는 폴더
// (회원 글은 회원 폴더 → 회원이 나중에 직접 수정해도 validateReviewBody 경로 검증 통과)
export function adminImageFolder(review: { id: string; user_id: string | null }) {
  return review.user_id ? `${review.user_id}/` : `ai/${review.id}/`;
}

export function validateReviewBody(body: unknown, userId: string) {
  const b = body as { title?: unknown; content?: unknown; imagePaths?: unknown } | null;
  const title = String(b?.title ?? '').trim();
  const content = String(b?.content ?? '').trim();
  const imagePaths = Array.isArray(b?.imagePaths) ? (b!.imagePaths as unknown[]).map(String) : [];

  if (!title || title.length > 100) return { error: '제목은 1~100자로 입력해주세요.' } as const;
  if (!content || content.length > 5000) return { error: '내용은 1~5000자로 입력해주세요.' } as const;
  if (imagePaths.length > MAX_REVIEW_IMAGES) {
    return { error: `사진은 최대 ${MAX_REVIEW_IMAGES}장까지 등록할 수 있습니다.` } as const;
  }
  // 본인 폴더의 파일만 허용 (경로 조작 방지)
  const ok = imagePaths.every(
    (p) => p.startsWith(`${userId}/`) && !p.includes('..') && p.split('/').length === 2
  );
  if (!ok) return { error: '잘못된 이미지 경로입니다.' } as const;

  return { title, content, imagePaths } as const;
}


export type ReviewRow = {
  id: string;
  user_id: string | null;
  title: string;
  content: string;
  image_paths: string[];
  is_hidden: boolean;
  is_ai_generated: boolean;
  is_interview: boolean;
  display_name: string | null;
  publish_at: string;
  created_at: string;
  member_profiles: ProfileEmbed;
};
